import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  GeneratedScenario,
  GeneratedPptContentSlide,
} from '../../models/wizard.models';

type GeneratedImagePreview = {
  id: string;
  title: string;
  moduleIndex?: number;
  mimeType: string;
  src: string;
  filename: string;
};

type DocumentPreviewMode = 'formatted' | 'raw';

@Component({
  selector: 'app-step-seven',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './step-seven.component.html',
  styleUrl: './step-seven.component.scss',
})
export class StepSevenComponent {
  @Input() moduleGenerationError: string | null = null;
  @Input() selectedScenario: GeneratedScenario | null = null;
  @Input() documentGenerated = false;
  @Input() isGeneratingDocument = false;
  @Input() documentGenerationError: string | null = null;
  @Input() targetImpactLevel: string | null = 'High';
  @Input() redTeamRequested = false;
  @Input() isRequestingRedTeamReview = false;
  @Input() redTeamError: string | null = null;
  @Input() critiqueApplied = false;
  @Input() isApplyingCritique = false;
  @Input() documentPreviewContent = '';
  @Input() redTeamCritiqueContent = '';
  @Input() pptPlanGenerated = false;
  @Input() pptContentGenerated = false;
  @Input() pptFileGenerated = false;
  @Input() isGeneratingPptPlan = false;
  @Input() isGeneratingPptPresentation = false;
  @Input() pptPlanError: string | null = null;
  @Input() pptPresentationError: string | null = null;
  @Input() pptContentSlides: GeneratedPptContentSlide[] = [];
  @Input() currentPptPreviewSlideIndex = 0;
  @Input() crisisImagesGenerated = false;
  @Input() isGeneratingCrisisImages = false;
  @Input() crisisImagesError: string | null = null;
  @Input() crisisImagePreviews: GeneratedImagePreview[] = [];

  @Input() generateDocument!: () => void;
  @Input() requestRedTeamReview!: () => void;
  @Input() applyCritique!: () => void;
  @Input() generatePowerPointPlan!: () => void;
  @Input() generatePowerPointPresentation!: () => void;
  @Input() downloadPptPresentation!: () => void;
  @Input() generateCrisisImages!: () => void;
  @Input() downloadCrisisImage!: (preview: GeneratedImagePreview) => void;
  @Input() goToStep!: (index: number) => void;

  documentPreviewMode: DocumentPreviewMode = 'formatted';

  setDocumentPreviewMode(mode: DocumentPreviewMode): void {
    this.documentPreviewMode = mode;
  }

  get documentPreviewRenderedHtml(): string {
    return this.renderMarkdownToHtml(this.documentPreviewContent);
  }

  get activePptSlide(): GeneratedPptContentSlide | null {
    if (this.pptContentSlides.length === 0) {
      return null;
    }

    const safeIndex = Math.max(
      0,
      Math.min(
        this.currentPptPreviewSlideIndex,
        this.pptContentSlides.length - 1,
      ),
    );

    return this.pptContentSlides[safeIndex] ?? null;
  }

  goToPreviousPptSlide(): void {
    if (this.pptContentSlides.length === 0) {
      return;
    }

    this.currentPptPreviewSlideIndex = Math.max(
      0,
      this.currentPptPreviewSlideIndex - 1,
    );
  }

  goToNextPptSlide(): void {
    if (this.pptContentSlides.length === 0) {
      return;
    }

    this.currentPptPreviewSlideIndex = Math.min(
      this.pptContentSlides.length - 1,
      this.currentPptPreviewSlideIndex + 1,
    );
  }

  selectPptSlide(index: number): void {
    if (index < 0 || index >= this.pptContentSlides.length) {
      return;
    }

    this.currentPptPreviewSlideIndex = index;
  }

  private renderMarkdownToHtml(markdown: string): string {
    const escaped = this.escapeHtml(markdown || '');
    const codeBlocks: string[] = [];

    const withCodePlaceholders = escaped.replace(
      /```([\s\S]*?)```/g,
      (_match, code: string) => {
        const html = `<pre><code>${code.trim()}</code></pre>`;
        const index = codeBlocks.push(html) - 1;
        return `__CODE_BLOCK_${index}__`;
      },
    );

    const lines = withCodePlaceholders.split('\n');
    const htmlLines: string[] = [];
    let inUnorderedList = false;
    let inOrderedList = false;

    const closeLists = () => {
      if (inUnorderedList) {
        htmlLines.push('</ul>');
        inUnorderedList = false;
      }
      if (inOrderedList) {
        htmlLines.push('</ol>');
        inOrderedList = false;
      }
    };

    for (const line of lines) {
      const trimmed = line.trim();

      if (!trimmed) {
        closeLists();
        continue;
      }

      const codeBlockMatch = trimmed.match(/^__CODE_BLOCK_(\d+)__$/);
      if (codeBlockMatch) {
        closeLists();
        htmlLines.push(trimmed);
        continue;
      }

      const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        closeLists();
        const level = headingMatch[1].length;
        const content = this.applyInlineMarkdown(headingMatch[2]);
        htmlLines.push(`<h${level}>${content}</h${level}>`);
        continue;
      }

      const unorderedListMatch = trimmed.match(/^[-*+]\s+(.*)$/);
      if (unorderedListMatch) {
        if (inOrderedList) {
          htmlLines.push('</ol>');
          inOrderedList = false;
        }
        if (!inUnorderedList) {
          htmlLines.push('<ul>');
          inUnorderedList = true;
        }
        htmlLines.push(
          `<li>${this.applyInlineMarkdown(unorderedListMatch[1])}</li>`,
        );
        continue;
      }

      const orderedListMatch = trimmed.match(/^\d+\.\s+(.*)$/);
      if (orderedListMatch) {
        if (inUnorderedList) {
          htmlLines.push('</ul>');
          inUnorderedList = false;
        }
        if (!inOrderedList) {
          htmlLines.push('<ol>');
          inOrderedList = true;
        }
        htmlLines.push(
          `<li>${this.applyInlineMarkdown(orderedListMatch[1])}</li>`,
        );
        continue;
      }

      closeLists();
      htmlLines.push(`<p>${this.applyInlineMarkdown(trimmed)}</p>`);
    }

    closeLists();

    let html = htmlLines.join('\n');
    codeBlocks.forEach((codeBlock, index) => {
      html = html.replace(`__CODE_BLOCK_${index}__`, codeBlock);
    });

    return html;
  }

  private applyInlineMarkdown(text: string): string {
    return text
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
